import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  createTableDateTimeColumn,
  createTableDefaultColumn,
  createTableLinkWithOpenNewTabColumn,
} from '@common/tableColumn.services';
import {
  colors,
  Column,
  ComponentType,
  createTableCell,
  createTableColumn,
  Grid,
  Icon,
  ResponsivePaginatedTable,
} from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import { useFetchTwcOptionsWithSearch } from '@userProfile/userProfile.queries';
import * as React from 'react';
import {
  useFetchCheckerCompanyOptions,
  useFetchCompanyOptions,
  useFetchDesignerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { ETransmittalSearchKey, ITransmittalItem, ITransmittalSearchFilter } from './transmittal.models';
import { TransmittalFormTypesOptions, TransmittalStatusOptions } from './transmittalFilterOptions.models';
import { useFetchTransmittal } from './transmittal.queries';
import { useTransmittalCreationModal } from './TransmittalCreationModal/transmittalCreationModal.hooks';
import { ETransmittalStatus, ETransmittalType } from './transmittal.constants';
import './TransmittalList.scss';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId } from '@config/config.hooks';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { ArrayParam, BooleanParam, NumberParam, StringParam } from 'use-query-params';
import {
  getDateTimeFilterFieldNameFrom,
  getDateTimeFilterFieldNameTo,
  getSelectFilterFieldNameId,
} from '@common/searchFilter/searchFilter.services';
import { DownloadButton } from '@file/DownloadButton';
import { StampIcon } from '@common/icons/StampIcon';
import { IButtonNavBarItem } from '@app/ButtonNavBar';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { exportTransmittalSearchExcelAsync } from './transmittal.services';
import { useAllowNewTransmittal } from './transmittalList.hooks';
import { ETransmittalPurposeCodeConst } from './transmittalPurpose/transmittalPurpose.constants';
import '@common/Common.scss';

export interface ITransmittalListProps {
  transmittalType?: ETransmittalType;
  transmittalTypeName?: string;
}

export const TransmittalList: React.FunctionComponent<ITransmittalListProps> = ({
  transmittalType,
  transmittalTypeName,
}) => {
  const projectId = useProjectId();

  const isAllowNewTransmittal = useAllowNewTransmittal(transmittalType);

  // #region Search Field Configs
  const TransmittalSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: ETransmittalSearchKey.transmittalNo,
        displayName: 'Transmittal No.',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.t1RefNo,
        displayName: 'T1 Ref No.',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.type,
        displayName: 'Type',
        type: SearchFilterType.TextSelect,
        textSelectOptions: TransmittalFormTypesOptions,
        disable: !!transmittalType,
        allowMultipleSelect: false,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.t1DesignPackage,
        displayName: 'T1 Design Package',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.sendDate,
        displayName: 'Date',
        type: SearchFilterType.DateTime,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.status,
        displayName: 'Status',
        type: SearchFilterType.TextSelect,
        textSelectOptions: TransmittalStatusOptions,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.sendFromProjectCompanyId,
        displayName: 'Send From',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.sendToProjectCompanyId,
        displayName: 'Send To',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchCompanyOptions({}),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.t1TwcId,
        displayName: 'T1 Nominated TWC',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.t1Designers,
        displayName: 'T1 Nominated Designer',
        type: SearchFilterType.CheckboxGroup,
        useFetchOptions: useFetchDesignerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
        allowMultipleSelect: true,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.t1Checkers,
        displayName: 'T1 Nominated Checker',
        type: SearchFilterType.CheckboxGroup,
        useFetchOptions: useFetchCheckerCompanyOptions({}),
        useFetchOptionsProps: { projectId },
        allowMultipleSelect: true,
      } as IFilterFieldConfig,
      {
        fieldName: ETransmittalSearchKey.twcReleased,
        displayName: 'Released for Construction',
        type: SearchFilterType.Boolean,
        booleanSelectOptions: [
          {
            key: 'All',
            text: 'All',
            data: undefined,
          },
          {
            key: 'Yes',
            text: 'Released',
            data: true,
          },
          {
            key: 'No',
            text: 'Pending',
            data: false,
          },
        ],
      } as IFilterFieldConfig,
    ];
  }, [transmittalType, projectId]);
  // #endregion Search Field Configs

  const userPreferenceAttributeName = React.useMemo(() => {
    switch (transmittalType) {
      case ETransmittalType.DESIGNER:
        return EUserProfilePreferenceAttributeName.DT_LIST_PAGE_SIZE;
      case ETransmittalType.SITE:
        return EUserProfilePreferenceAttributeName.ST_LIST_PAGE_SIZE;
      case ETransmittalType.ICE:
        return EUserProfilePreferenceAttributeName.CT_LIST_PAGE_SIZE;
      case ETransmittalType.REVIEWER:
        return EUserProfilePreferenceAttributeName.RT_LIST_PAGE_SIZE;
      default:
        return undefined;
    }
  }, [transmittalType]);

  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(userPreferenceAttributeName);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: 'sendDate',
    isDesc: true,
  });
  const { navigate, generateRoutePath, projectNo } = useNavigate();

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [ETransmittalSearchKey.transmittalNo]: StringParam,
      [ETransmittalSearchKey.t1RefNo]: StringParam,
      [ETransmittalSearchKey.t1DesignPackage]: StringParam,
      [ETransmittalSearchKey.twcReleased]: BooleanParam,
      [`${getDateTimeFilterFieldNameFrom(ETransmittalSearchKey.sendDate)}`]: StringParam,
      [`${getDateTimeFilterFieldNameTo(ETransmittalSearchKey.sendDate)}`]: StringParam,
      [ETransmittalSearchKey.status]: StringParam,
      [ETransmittalSearchKey.type]: StringParam,
      [`${getSelectFilterFieldNameId(ETransmittalSearchKey.sendFromProjectCompanyId)}`]: NumberParam,
      [`${getSelectFilterFieldNameId(ETransmittalSearchKey.sendToProjectCompanyId)}`]: NumberParam,
      [ETransmittalSearchKey.t1TwcId]: StringParam,
      [ETransmittalSearchKey.t1Designers]: ArrayParam,
      [ETransmittalSearchKey.t1Checkers]: ArrayParam,
    },
    TransmittalSearchFieldConfigs,
    transmittalType ? { type: transmittalType } : {},
  );
  const transmittalFilterObj = React.useMemo(() => {
    const temp = {} as any;

    Object.keys(localFilter).forEach((key) => {
      if (!key.includes('.')) temp[key] = localFilter[key];
      else {
        const splitedKey = key.split('.');
        const child = localFilter[splitedKey[0]] ?? {};
        child[splitedKey[1]] = localFilter[key];
        temp[splitedKey[0]] = child;
      }
    });

    return temp as ITransmittalSearchFilter;
  }, [JSON.stringify(localFilter)]);

  const {
    data: transmittalPagedResult,
    isLoading: isLoadingTransmittalSearch,
    isRefetching: isRefetchingTransmittalSearch,
    error: errorTransmittalSearch,
  } = useFetchTransmittal({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {}),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as ITransmittalSearchFilter) : undefined,
  });

  const {
    renderModal: renderTransmittalCreationModal,
    openPortal: openTransmittalCreationModal,
    closePortal: closeTransmittalCreationModal,
  } = useTransmittalCreationModal({ projectId });

  React.useEffect(() => {
    setLocalFilter((old) => (transmittalType ? { ...old, type: transmittalType } : old));
    setPageNumber(1);
  }, [transmittalType]);

  // #region Table Columns
  const MasterScheduleTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableDateTimeColumn('sendDate', 'Date'),
      createTableLinkWithOpenNewTabColumn<ITransmittalItem>(
        'transmittalNo',
        'Transmittal No.',
        ({ item }) => {
          switch (item.type) {
            case ETransmittalType.DESIGNER:
              return generateRoutePath(routes.transmittals.designerTransmittal.item.path, { id: item.id.toString() });
            case ETransmittalType.SITE:
              return generateRoutePath(routes.transmittals.siteTransmittal.item.path, { id: item.id.toString() });
            case ETransmittalType.ICE:
              return generateRoutePath(routes.transmittals.checkerTransmittal.item.path, { id: item.id.toString() });
            case ETransmittalType.REVIEWER:
            default:
              return generateRoutePath(routes.transmittals.lirTransmittal.item.path, { id: item.id.toString() });
          }
        },
        ({ value, item, index, cellExtras }, link) => {
          if (item.isArchived) {
            return (
              <Grid container>
                {link}
                <span className="gcl-gems-transmittal-list-tag gcl-ml-xs">
                  <Icon iconName="Archive" size={12} className="gcl-mr-xs" /> Archived
                </span>
              </Grid>
            );
          }
          return link;
        },
      ),
      createTableDefaultColumn('type', 'Type'),
      createTableColumn({
        accessor: 'status',
        Header: 'Status',
        disableResizing: true,
        disableSortBy: false,
        Cell: createTableCell<string, ITransmittalItem>(
          (value: string | undefined, item: ITransmittalItem, index: number, cellExtras: { [key: string]: any }) => {
            if (value === ETransmittalStatus.APPROVED)
              return <Icon iconName="CheckCircleFill" className="gcl-gems-transmittal-list-released-icon" />;
            if (value === ETransmittalStatus.DRAFT)
              return <Icon iconName="RecordCircleFill" className="gcl-gems-transmittal-list-not-released-icon" />;
            if (value === ETransmittalStatus.PENDING_FOR_RFC) return <StampIcon size={12} />;
            return <Icon iconName="Dash" />;
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<ITransmittalItem>(
        'masterSchedule.refNo',
        'T1',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.masterSchedule.id }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableDefaultColumn('masterSchedule.designPackage', 'Design Package'),
      createTableColumn({
        accessor: 'sendFromProjectCompany.name',
        Header: 'From',
        disableResizing: true,
        disableSortBy: false,
        Cell: createTableCell<string | undefined, ITransmittalItem>(
          (value: string | undefined, item: ITransmittalItem, index: number, cellExtras: { [key: string]: any }) => {
            return <>{item.sendFromProjectCompany.companyType?.id === ECompanyTypeIdConst.PLACEHOLDER ? '' : value}</>;
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'sendToProjectCompany.name',
        Header: 'To',
        disableResizing: true,
        disableSortBy: false,
        Cell: createTableCell<string | undefined, ITransmittalItem>(
          (value: string | undefined, item: ITransmittalItem, index: number, cellExtras: { [key: string]: any }) => {
            return <>{item.sendToProjectCompany.companyType?.id === ECompanyTypeIdConst.PLACEHOLDER ? '' : value}</>;
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'purpose.code',
        Header: 'Purpose',
        Cell: createTableCell<string | undefined, ITransmittalItem>(
          (value: string | undefined, item: ITransmittalItem, index: number, cellExtras: { [key: string]: any }) => {
            return <>{item.purpose.code === ETransmittalPurposeCodeConst.SE ? '' : value}</>;
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'twcReleased',
        Header: 'Released for Construction',
        disableResizing: true,
        disableSortBy: false,
        Cell: createTableCell<boolean, ITransmittalItem>(
          (value: boolean | undefined, item: ITransmittalItem, index: number, cellExtras: { [key: string]: any }) => {
            if (value === true)
              return <Icon iconName="CheckCircleFill" className="gcl-gems-transmittal-list-released-icon" />;
            if (value === false) return <StampIcon size={12} color={colors.danger500} />;

            return <>N/A</>;
          },
        ),
      } as Column),
    ];
  }, []);
  // #endregion Table Columns
  return (
    <>
      <SearchPageBase
        data={transmittalPagedResult?.items}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            text: `Search ${transmittalTypeName ? `${transmittalTypeName} ` : ''}Transmittal`,
            prefixIconName: 'Search',
            active: true,
          },
        ]}
        title={`${transmittalTypeName ? `${transmittalTypeName} ` : ''}Transmittal List`}
        isTableRefetching={isRefetchingTransmittalSearch}
        isTableLoading={isLoadingTransmittalSearch}
        buttonNavBarItem={[
          ...(isAllowNewTransmittal
            ? ([
                {
                  text: 'New',
                  onClick: () => openTransmittalCreationModal(transmittalType),
                  iconName: 'FileEarmarkPlus',
                  componentType: ComponentType.Primary,
                  showNoneInArchivedProject: true,
                },
              ] as IButtonNavBarItem[])
            : []),
          {
            text: 'Export',
            iconName: 'ArrowBarDown',
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                exportFunction={exportTransmittalSearchExcelAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  filter: transmittalFilterObj,
                }}
              />
            ),
          },
        ]}
        searchFilter={
          <SearchFilter
            fieldConfigs={TransmittalSearchFieldConfigs}
            onFilterChanged={(filter) => {
              setLocalFilter(filter);
            }}
            defaultFilter={localFilter}
          />
        }
        appliedSearchFilter={localFilter}
        noContentLabel="No Transmittal created yet"
        onRenderTable={() => {
          return (
            <>
              <ApiErrorDisplay
                errors={[{ error: errorTransmittalSearch, genericError: 'Get transmittal list failed' }]}
              />
              <ResponsivePaginatedTable
                classNames={{ body: 'gcl-gems-list-table-body' }}
                columns={MasterScheduleTableColumns}
                paginationProps={{
                  pageIndex: pageNumber - 1,
                  pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
                  paginatorPosition: 'top',
                  totalCount: transmittalPagedResult?.totalCount || 0,
                  initialPageSize: initPageSize,
                  onPageChange: async (pageIndex, newPageSize) => {
                    setPageNumber(pageIndex + 1);
                    setPageSize(newPageSize);
                  },
                }}
                sortingProps={{
                  initialSortBy:
                    sortByQuery && sortByQuery.sortByField
                      ? [
                          {
                            id: sortByQuery.sortByField,
                            desc: !!sortByQuery.isDesc,
                          },
                        ]
                      : [],
                  onSort: (sortBy) => {
                    if (sortBy && sortBy.length >= 1)
                      setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
                    else setSortByQuery(undefined);
                  },
                }}
                allowSelectRow={false}
                items={transmittalPagedResult?.items || []}
                allowExpandRow={false}
              />
            </>
          );
        }}
        legend={
          <Grid container spacing={2}>
            {(!transmittalType || transmittalType === ETransmittalType.DESIGNER) && (
              <Grid item xs={12} sm={6} md={3}>
                <div>
                  <b>Designer Transmittal</b>
                </div>
                <ul>
                  <li>I - For Information</li>
                  <li>R - For Review/Comment</li>
                  <li>P - For Pricing</li>
                  <li>S - For Onward Submission</li>
                  <li>O - Other (please specify)</li>
                </ul>
              </Grid>
            )}
            {(!transmittalType || transmittalType === ETransmittalType.SITE) && (
              <Grid item xs={12} sm={6} md={3}>
                <div>
                  <b>Site Transmittals</b>
                </div>
                <ul>
                  <li>A - For Approval / Certification</li>
                  <li>I - For Information</li>
                  <li>O - Other (please specify)</li>
                </ul>
              </Grid>
            )}
            {(!transmittalType || transmittalType === ETransmittalType.ICE) && (
              <Grid item xs={12} sm={6} md={3}>
                <div>
                  <b>Checker Transmittals</b>
                </div>
                <ul>
                  <li>A - Approved</li>
                  <li>O - Approval Pending</li>
                </ul>
              </Grid>
            )}
            {(!transmittalType || transmittalType === ETransmittalType.REVIEWER) && (
              <Grid item xs={12} sm={6} md={3}>
                <div>
                  <b>Lambeth Independent Review</b>
                </div>
                <ul>
                  <li>R - For Review/Comment</li>
                  <li>O - Other (please specify)</li>
                </ul>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <b>Status</b>
              </div>
              <ul>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-transmittal-list-released-icon" /> - Approved
                </li>
                <li>
                  <Icon iconName="RecordCircleFill" className="gcl-gems-transmittal-list-not-released-icon" /> - Draft
                </li>
              </ul>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div>
                <b>Released for Construction</b>
              </div>
              <ul>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-transmittal-list-released-icon" /> - Released
                </li>
                <li>
                  <StampIcon size={12} color={colors.danger500} /> - Pending for RFC
                </li>
              </ul>
            </Grid>
          </Grid>
        }
      />
      {renderTransmittalCreationModal({})}
    </>
  );
};
